import React, { useState, useEffect } from 'react';
import {
    Row, Col, Form, FormGroup, Label, Input, Card, CardBody,
    Container, Button, FormFeedback
} from "reactstrap";

import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import "./CustomCheckbox.css";

const DriverDocumentsTypesEdit = () => {
    const { id } = useParams();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [titleArabic, setTitleArabic] = useState('');
    const [descriptionArabic, setDescriptionArabic] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);

    const navigate = useNavigate();

    const [validation, setValidation] = useState({
        title: null,
        description: null,
        titleArabic: null,
        descriptionArabic: null,
    });

    const [checkboxes, setCheckboxes] = useState({
        is_upload: false,
        is_required: false,
        is_front_required: false,
        is_back_required: false,
    });

    useEffect(() => {
        if (id) {
            fetchDocumentType();
        }
    }, [id]);

    const fetchDocumentType = async () => {
        try {
            setFetchLoading(true);
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/drivers-documents-types/${id}`, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (response.status === 200 && data?.data) {
                const item = data.data;
                setTitle(item.document_type_name || '');
                setDescription(item.document_type_description || '');
                setTitleArabic(item.document_type_name_arabic || '');
                setDescriptionArabic(item.document_type_description_arabic || '');
                setCheckboxes({
                    is_upload: item.is_upload_allowed == 1,
                    is_required: item.is_required == 1,
                    is_front_required: item.front_required == 1,
                    is_back_required: item.back_required == 1,
                });
            } else {
                toast.error(data?.message || 'Failed to load document type', { autoClose: 3000 });
                navigate('/drivers-documents-types');
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load document type", { autoClose: 3000 });
            navigate('/drivers-documents-types');
        } finally {
            setFetchLoading(false);
        }
    };

    const handleCheckboxChange = (key) => {
        setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const validateFields = () => {
        let isValid = true;
        const newValidation = {};

        newValidation.title = title.trim() !== '';
        newValidation.description = description.trim() !== '';
        newValidation.titleArabic = titleArabic.trim() !== '';
        newValidation.descriptionArabic = descriptionArabic.trim() !== '';

        setValidation(newValidation);

        Object.values(newValidation).forEach(v => {
            if (v !== true) isValid = false;
        });

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFields()) return;

        setLoading(true);

        const payload = {
            title,
            description,
            title_arabic: titleArabic,
            description_arabic: descriptionArabic,
            ...checkboxes,
        };

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_WEB_APP_URL}/api/web/drivers-documents-types/update/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.status === 200) {
                toast.success('Document type updated successfully', { autoClose: 3000 });
                navigate(`/drivers-documents-types`);
            } else if (response.status === 422) {
                if (data?.errors) {
                    Object.values(data.errors).forEach((errorArr) => {
                        errorArr.forEach((errMsg) => {
                            toast.error(errMsg, { autoClose: 3000 });
                        });
                    });
                } else {
                    toast.error(data?.message || 'Validation failed', { autoClose: 3000 });
                }
            } else {
                toast.error(data?.message || `Error: ${response.statusText}`, { autoClose: 3000 });
            }
        } catch (err) {
            console.log(err);
            toast.error("Failed to update type", { autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    document.title = "Edit Documents Type (Drivers) | Moeen";

    if (fetchLoading) {
        return (
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs maintitle="Home" title="Edit Documents Type (Drivers)" />
                    <div className="text-center p-5">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs maintitle="Home" title="Edit Documents Type (Drivers)" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            {/* Title */}
                                            <div className="mb-3">
                                                <Label>Title</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter title"
                                                    value={title}
                                                    onChange={e => setTitle(e.target.value)}
                                                    valid={validation["title"] === true}
                                                    invalid={validation["title"] !== true && validation["title"] !== null}
                                                />
                                                <FormFeedback tooltip>Title is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Description */}
                                            <div className="mb-3">
                                                <Label>Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter description"
                                                    value={description}
                                                    onChange={e => setDescription(e.target.value)}
                                                    valid={validation["description"] === true}
                                                    invalid={validation["description"] !== true && validation["description"] !== null}
                                                />
                                                <FormFeedback tooltip>Description is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Title Arabic */}
                                            <div className="mb-3">
                                                <Label>Title Arabic</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter title arabic"
                                                    value={titleArabic}
                                                    onChange={e => setTitleArabic(e.target.value)}
                                                    valid={validation["titleArabic"] === true}
                                                    invalid={validation["titleArabic"] !== true && validation["titleArabic"] !== null}
                                                />
                                                <FormFeedback tooltip>Title Arabic is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="6">
                                            {/* Description Arabic */}
                                            <div className="mb-3">
                                                <Label>Description Arabic</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Enter description arabic"
                                                    value={descriptionArabic}
                                                    onChange={e => setDescriptionArabic(e.target.value)}
                                                    valid={validation["descriptionArabic"] === true}
                                                    invalid={validation["descriptionArabic"] !== true && validation["descriptionArabic"] !== null}
                                                />
                                                <FormFeedback tooltip>Description Arabic is required.</FormFeedback>
                                            </div>
                                        </Col>

                                        <Col md="12">
                                            <div className="d-flex flex-wrap gap-2">
                                                {[
                                                    { label: "Is Upload", key: "is_upload" },
                                                    { label: "Is Required", key: "is_required" },
                                                    { label: "Front Required", key: "is_front_required" },
                                                    { label: "Back Required", key: "is_back_required" },
                                                ].map(({ label, key }) => (
                                                    <div
                                                        key={key}
                                                        className={`custom-box-checkbox ${checkboxes[key] ? "active" : ""
                                                            }`}
                                                        onClick={() => handleCheckboxChange(key)}
                                                    >
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="text-end">
                                            <Button type="submit" color="primary" disabled={loading}>
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                    </>
                                                ) : (
                                                    "Update"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default DriverDocumentsTypesEdit;


